"use client";

import { useState, useEffect, useRef, Fragment, ReactNode } from 'react';
import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Rocket, TrendingUp, Gem, ChevronLeft, ChevronRight, ArrowRight, Check, Building, Blocks
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import DynamicIcon from './dynamicIcon';
import { formatServiceName } from '@/app/components/formatServiceName';

// --- HELPER AND TYPE DEFINITIONS ---

// Helper function to concatenate class names
function classNames(...classes: (string | boolean | null | undefined)[]): string {
    return classes.filter(Boolean).join(' ');
}

// Interface for select component items
interface SelectItem {
    id: number;
    name: string;
    value: string;
}

// Interface for the props of the custom select component
interface CustomSelectProps {
    label: ReactNode;
    data: SelectItem[];
    selected: SelectItem;
    onChange: (value: SelectItem) => void;
}

// --- DATA INTERFACES ---
interface ServiceDetail {
    serviceName: string;
}

interface Bundle {
    id: number;
    bundleName: string;
    description: string | null;
    Services: ServiceDetail[];
}

interface OurService {
    icon: string | null;
    serviceName: string;
    summary: string | null;
}

interface TrainingProgramData {
    title: string;
    certifications: number;
    imageUrl: string | null; 
    description?: string | null;
}

interface HeroProps {
    bundles: Bundle[];
    ourServices: OurService[];
    trainingProgramsData: TrainingProgramData[];
}

interface FeatureDetail {
    text: string;
    highlight?: boolean;
}

interface FullWidthSlide {
    layout: 'full-width';
    image: string;
    alt: string;
}

interface DefaultSlide {
    title: string;
    description: string;
    image: string;
    alt: string;
    buttonText: string;
    buttonLink: string;
    layout?: undefined; // This makes it clear this type doesn't have the 'full-width' layout
}

type HeroSlide = FullWidthSlide | DefaultSlide;

// Reusable Custom Select Component
const CustomSelect = ({ id, label, data, selected, onChange }: CustomSelectProps & { id?: string }) => {
    return (
        <div id={id}>
            <label className="block text-sm md:text-base font-semibold text-slate-700 mb-1">
                {label}
            </label>
            <Listbox value={selected} onChange={onChange}>
                <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-200 bg-white py-2 pl-4 pr-10 text-left text-slate-800 focus:outline-none text-sm md:text-base">
                        <span className="block truncate">{selected.name}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-4 w-4 md:h-5 md:w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </span>
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none text-xs md:text-sm border border-gray-200">
                            {data.map((item) => (
                                <Listbox.Option
                                    key={item.id}
                                    className={({ active }) =>
                                        classNames(
                                            'relative cursor-default select-none py-2 pl-10 pr-4',
                                            active ? 'bg-slate-100 text-indigo-900 rounded-sm' : 'text-gray-900'
                                        )
                                    }
                                    value={item}
                                >
                                    {({ selected: isSelected }) => (
                                        <>
                                            <span
                                                className={classNames(
                                                    'block truncate',
                                                    isSelected ? 'font-semibold' : 'font-normal'
                                                )}
                                            >
                                                {item.name}
                                            </span>
                                            {isSelected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                                    <CheckIcon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
};


// HELPER COMPONENT FOR THE COUNT-UP ANIMATION
const FRAME_RATE = 1000 / 60; // 60fps

const Counter = ({ end, duration, start, suffix = '' }: { end: number; duration: number; start: boolean; suffix?: string }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!start) return;

        const totalFrames = Math.round(duration / FRAME_RATE);
        let currentFrame = 0;

        const counter = setInterval(() => {
            currentFrame++;
            const progress = currentFrame / totalFrames;
            const currentVal = Math.round(end * progress);
            setCount(currentVal);

            if (currentFrame === totalFrames) {
                clearInterval(counter);
                setCount(end);
            }
        }, FRAME_RATE);

        return () => clearInterval(counter);
    }, [start, end, duration]);

    return (
        <span className="inline-block">
            {count}{suffix}
        </span>
    );
};

// --- DATA FOR THE PAGE ---

const heroSlides: HeroSlide[] = [
    // {
    //     layout: 'full-width',
    //     image: '/hero.png',
    //     alt: 'IDECS25',
    // },
    {
        title: 'SDeMARKETPLACE',
        description: 'Empowering small and medium enterprises to scale through digital transformation. Your digital transformation starts here!',
        image: '/hero1.png',
        alt: 'SDeMARKETPLACE',
        buttonText: 'Start Your Journey Today',
        buttonLink: '#landing-section'
    },
    {
        title: 'Sarawak Digital Bundles for Every Business',
        description: 'Choose from tailored digital bundles designed to fit your business needs. Scale smarter, faster, and more efficiently with the right tools!',
        image: '/hero2.png',
        alt: 'Sarawak Digital Bundles',
        buttonText: 'Explore More',
        buttonLink: '/login'
    },
    {
        title: 'Not Sure Where to Start? No Worries!',
        description: 'Let us guide your digital journey with expert support and simple steps. Begin with confidence and transform your business with ease!',
        image: '/hero3.png',
        alt: 'Sarawak Digital Support',
        buttonText: 'Get Support',
        buttonLink: '/support'
    }
];

const enterpriseFeatures = [
    { text: 'Special Features:' },
    { text: 'Everything in Premium', highlight: true },
    { text: 'AI Agent' },
    { text: 'Automated Workflows' },
    { text: 'Advanced Business Intelligence (BI) Platform' },
    { text: 'Operational Support System (OSS)' },
    { text: 'Business Support System (BSS)' },
    { text: 'Enterprise Resource Planning (ERP) System' },
];

const businessSizes: SelectItem[] = [
    { id: 1, name: '1-20', value: '1-20' },
    { id: 2, name: '21-50', value: '21-50' },
    { id: 3, name: '51-100', value: '51-100' },
    { id: 4, name: '101-250', value: '101-250' },
    { id: 5, name: '251-1000', value: '251-1000' },
    { id: 6, name: '1000+', value: '1000+' },
];

const HeroBefore: FC<HeroProps> = ({ bundles = [], ourServices = [], trainingProgramsData = [] }) => {
    const [showEnterprise, setShowEnterprise] = useState(false);
    const statsSectionRef = useRef<HTMLDivElement>(null);
    const [startAnimation, setStartAnimation] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    const [enterpriseForm, setEnterpriseForm] = useState({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        businessSize: { id: 0, name: 'Select business size', value: '' } as SelectItem, // Added
    });
    const [errors, setErrors] = useState({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        businessSize: '',
    });

    useEffect(() => {
        const fieldOrder: (keyof typeof errors)[] = [
            'fullName',
            'email',
            'companyName',
            'phoneNumber',
            'businessSize'
        ];

        const errorFieldIdMap = {
            fullName: 'full-name',
            email: 'work-email',
            companyName: 'company-name-enterprise',
            phoneNumber: 'phone-number',
            businessSize: 'businessSize'
        };
        const firstErrorFieldKey = fieldOrder.find(field => errors[field]);
        if (firstErrorFieldKey) {
            const elementId = errorFieldIdMap[firstErrorFieldKey];
            if (elementId) {
                const element = document.getElementById(elementId);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        }
    }, [errors]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const trainingPrograms = trainingProgramsData.map(program => ({
        image: program.imageUrl || '/defaultImage.jpg', // Fallback image if DB is null
        title: program.title,
        certifications: program.certifications,
        description: program.description || 'No description available.',
    }));

    // Hardcoded icons for the three main bundles
    const bundleIcons = [
        <Blocks key="icon-foundation" className="h-6 w-6 text-indigo-600" />,
        <Rocket key="icon-essential" className="h-6 w-6 text-indigo-600" />,
        <TrendingUp key="icon-growth" className="h-6 w-6 text-indigo-600" />,
        <Gem key="icon-premium" className="h-6 w-6 text-indigo-600" />,
    ];

    // Transform fetched bundle data into the structure required by the UI
    const features = bundles.map((bundle, index) => {
        const details: FeatureDetail[] = bundle.Services.map(service => ({ text: service.serviceName }));

        if (bundle.bundleName === 'Essential') {
            details.unshift({ text: 'Everything in Foundation', highlight: true });
        } else if (bundle.bundleName === 'Growth') {
            details.unshift({ text: 'Everything in Essential', highlight: true });
        } else if (bundle.bundleName === 'Premium') {
            details.unshift({ text: 'Everything in Growth', highlight: true });
        }

        return {
            icon: bundleIcons[index] || <Gem className="h-6 w-6 text-indigo-600" />, // Fallback icon
            title: bundle.bundleName,
            description: bundle.description,
            details: details,
        };
    });

    const validateField = (name: string, value: string | SelectItem) => { // Modified
        let error = '';
        switch (name) {
            case 'fullName':
                if (!value) {
                    error = 'Full name is required.';
                } else if (typeof value === 'string' && !/^[A-Z\s.'-/@]+$/.test(value)) {
                    error = "Full name can only contain letters, spaces, and characters .'-/@.";
                } else if (typeof value === 'string' && value.trim().length < 3) {
                    error = "Full name must be at least 3 characters long.";
                }
                break;
            case 'email':
                if (!value) {
                    error = 'Email is required.';
                } else if (typeof value === 'string' && !/\S+@\S+\.\S+/.test(value)) {
                    error = 'Please enter a valid email address.';
                }
                break;
            case 'companyName':
                if (!value) error = 'Company name is required.';
                break;
            case 'phoneNumber':
                if (!value) {
                    error = 'Phone number is required.';
                } else if (typeof value === 'string' && !/^6\d{10,11}$/.test(value)) {
                    error = 'Phone number must be 11 or 12 digits and start with 6.';
                }
                break;
            case 'businessSize': // Added
                if (typeof value === 'object' && !value.value) {
                    error = 'Business size is required.';
                }
                break;
            default:
                break;
        }
        return error;
    };

    const handleShowEnterpriseClick = () => {
        setShowEnterprise(true);
        // Use a short timeout to ensure the section is rendered before we try to scroll.
        setTimeout(() => {
            document.getElementById('enterprise-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && !startAnimation) {
                    setStartAnimation(true);
                    observer.disconnect();
                }
            },
            {
                root: null,
                threshold: 0.2,
            }
        );

        const currentRef = statsSectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [startAnimation]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const newErrors = {
            fullName: validateField('fullName', enterpriseForm.fullName),
            email: validateField('email', enterpriseForm.email),
            companyName: validateField('companyName', enterpriseForm.companyName),
            phoneNumber: validateField('phoneNumber', enterpriseForm.phoneNumber),
            businessSize: validateField('businessSize', enterpriseForm.businessSize), // Added
        };

        setErrors(newErrors);
        const isValid = Object.values(newErrors).every(error => error === '');

        if (isValid) {
            setIsSubmitting(true);
            const toastId = toast.loading('Sending your email...', {
                duration: Infinity,
            });

            try {
                const response = await fetch('/api/enterprise', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...enterpriseForm,
                        businessSize: enterpriseForm.businessSize.name, // Send the selected name string
                    }),
                });

                if (response.ok) {
                    toast.success('Thank you! Your inquiry has been sent.', {
                        id: toastId,
                        duration: 2000
                    });

                    // Reset form fields after successful submission
                    setEnterpriseForm({
                        fullName: '',
                        email: '',
                        companyName: '',
                        phoneNumber: '',
                        businessSize: { id: 0, name: 'Select business size', value: '' },
                    });
                } else {
                    const data = await response.json();
                    toast.error(data.error || 'Failed to send message. Please try again.', {
                        id: toastId,
                        duration: 3000
                    });
                }
            } catch (error) {
                console.error('Enterprise form submission error:', error);
                toast.error('An unexpected error occurred. Please try again.', {
                    id: toastId,
                    duration: 3000
                });
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.log('Form has errors, submission prevented.');
        }
    };

    const handleNext = () => {
        const atEnd = isMobile
            ? currentIndex >= trainingPrograms.length - 1
            : currentIndex + 3 >= trainingPrograms.length;

        if (!atEnd) {
            setCurrentIndex(prevIndex => prevIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prevIndex => prevIndex - 1);
        }
    };

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, [])

    useEffect(() => {
        if (window.location.hash === '#dash-top') {
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    // Dynamically split the ourServices array into two halves
    const midpoint = Math.ceil(ourServices.length / 2);
    const servicesRow1 = ourServices.slice(0, midpoint);
    const servicesRow2 = ourServices.slice(midpoint);

    return (
        <>
            <section id="dash-top" className="scroll-mt-32 relative bg-slate-100 pt-12 md:pt-40 pb-20">
                <div className="container mx-auto px-4 md:-mt-14">
                    <div className="grid grid-cols-1 md:grid-cols-5 items-center gap-8 max-w-6xl mx-auto">
                        {/* Left: Text Content */}
                        <div className="md:col-span-2 px-2 md:px-0 order-2 md:order-1">
                            <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 tracking-tight leading-tight">
                                Empowering SMEs to Scale Through Digital Transformation
                            </h1>
                            <p className="text-base md:text-lg text-gray-500 mb-6 tracking-tight leading-tight">
                                Scale smarter, faster, and more efficiently with tailored digital bundles and expert hands on support
                            </p>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-row gap-3 items-stretch">
                                <Link href="/login" className="flex-1 flex">
                                    <button className="w-full h-full bg-indigo-600 text-white font-semibold py-2.5 px-3 rounded-lg border-2 border-indigo-600 hover:bg-indigo-700 hover:border-indigo-700 transition-colors shadow-lg shadow-indigo-500/20 text-sm md:text-base hover:cursor-pointer">
                                    Register for Free
                                    </button>
                                </Link>
                                <Link href="/support" className="flex-1 flex">
                                    <button className="w-full h-full border-2 border-indigo-600 text-indigo-600 font-semibold py-2.5 px-3 rounded-lg hover:bg-indigo-100 transition-colors text-sm md:text-base hover:cursor-pointer">
                                    SDeMarketplace FAQ
                                    </button>
                                </Link>
                                </div>
                                <p className="text-xs md:text-sm text-indigo-600 font-semibold text-center tracking-tight leading-tight">
                                    Sign up to get latest update on sme digitalization
                                </p>
                                <p className="text-[11px] md:text-xs text-gray-500 text-center tracking-tight leading-tight">
                                    Powered by <strong>SDEC</strong> | Operated by <strong>FIT Pioneer</strong>
                                </p>
                            </div>
                        </div>
                        {/* Right: Static Image */}
                        <div className="md:col-span-3 order-1 md:order-2 flex justify-center">
                            <div className="rounded-2xl shadow-lg overflow-hidden">
                                <Image
                                    src="/hero.png"
                                    alt="Empowering SMEs through Digital Transformation"
                                    width={574}
                                    height={443}
                                    priority
                                    quality={75}
                                    className="max-w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="landing-section" className="scroll-mt-10 bg-white py-16 md:py-24 overflow-hidden">
                <div className="container mx-auto px-4">
                    {/* Header Section */}
                    <div className="max-w-3xl mx-auto text-center mb-28 relative">
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                            Digitalize your business <br />
                            <span className="relative inline-block text-indigo-600">
                                with us today
                                
                                {/* Exact Hand-Drawn SVG Underline */}
                                <div className="absolute left-1/2 -bottom-6 -translate-x-[25%] w-full max-w-[320px] md:max-w-[371px] pointer-events-none select-none">
                                    <svg viewBox="0 0 371 33" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                                        <path 
                                            d="M13 10.2 C58 7.6, 109 7.1, 154 6.5 C207 5.7, 264 7.1, 338 13.2" 
                                            stroke="#3B36FF" 
                                            strokeWidth="1.4" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                        />
                                        <path 
                                            d="M12.5 10.8 C53 8.2, 101 6.3, 145 7.4 C191 8.5, 233 11.8, 279 17.4" 
                                            stroke="#3B36FF" 
                                            strokeWidth="1.2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            opacity="0.95"
                                        />
                                        <path 
                                            d="M14 9.5 C62 6.2, 117 5.2, 169 6.2 C220 7.1, 270 8.7, 321 11.8" 
                                            stroke="#3B36FF" 
                                            strokeWidth="0.8" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            opacity="0.65"
                                        />
                                    </svg>
                                </div>
                            </span>
                        </h2>
                        <p className="mt-8 text-slate-500 font-medium max-w-lg mx-auto text-base md:text-lg tracking-tight leading-tight">
                            End-to-end digital solution and expert guidance to help your business grow, compete, and lead in digital era
                        </p>
                    </div>

                    {/* Animated Grid Container */}
                    <motion.div 
                        variants={{
                            hidden: {},
                            visible: {
                                transition: {
                                    staggerChildren: 0.3 // Staggers animations from left to right
                                }
                            }
                        }}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-28 gap-x-8 max-w-[280px] md:max-w-7xl mx-auto pt-6"
                    >
                        {[
                            {
                                id: '01',
                                title: <>Essential Tools &amp;<br />Expert Support</>,
                                description: 'Seamlessly integrate powerful digital solutions into your business with our hands on guidance.',
                                image: '/digitalize1.png',
                                badgeColor: 'text-indigo-600 bg-indigo-50',
                                ringColor: 'border-indigo-500',
                                barGradient: 'from-indigo-600 via-indigo-400 to-indigo-100/10'
                            },
                            {
                                id: '02',
                                title: <>Growth Focused<br />Strategies</>,
                                description: 'Optimize your internal operations, skyrocket your sales, and build a dominant online presence.',
                                image: '/digitalize2.png',
                                badgeColor: 'text-sky-600 bg-sky-50',
                                ringColor: 'border-sky-400',
                                barGradient: 'from-sky-500 via-sky-300 to-sky-100/10'
                            },
                            {
                                id: '03',
                                title: <>Tailored for<br />Sarawak</>,
                                description: "Overcome unique local challenges with solutions built specifically for our market's distinct opportunities.",
                                image: '/digitalize3.png',
                                badgeColor: 'text-emerald-600 bg-emerald-50',
                                ringColor: 'border-emerald-500',
                                barGradient: 'from-emerald-500 via-emerald-300 to-emerald-100/10'
                            },
                            {
                                id: '04',
                                title: <>Personalized<br />Partnership</>,
                                description: 'Gain a trusted guide committed to making your business more efficient, competitive, and profitable.',
                                image: '/digitalize4.png',
                                badgeColor: 'text-amber-600 bg-amber-50',
                                ringColor: 'border-amber-400',
                                barGradient: 'from-amber-500 via-amber-300 to-amber-100/10'
                            }
                        ].map((card) => (
                            <motion.div
                                key={card.id}
                                variants={{
                                    hidden: { opacity: 0, y: 60 },
                                    visible: { 
                                        opacity: 1, 
                                        y: 0, 
                                        transition: { duration: 1.5, ease: [0.25, 1, 0.5, 1] } 
                                    }
                                }}
                                className="relative bg-white border border-slate-100 rounded-3xl p-8 pt-20 md:pt-28 shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col justify-between h-full transition-shadow duration-300 hover:shadow-[0_25px_60px_rgba(0,0,0,0.20)]"
                            >
                                {/* Circle Image Wrapper */}
                                <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-36 h-36 md:w-44 md:h-44 rounded-full bg-white shadow-xl z-10 overflow-hidden">
                                    <div className="relative w-full h-full rounded-full overflow-hidden bg-white border-2 border-white z-10">
                                        <Image
                                            src={card.image}
                                            alt={card.id}
                                            fill
                                            className="object-cover"
                                        />
                                    </div>
                                </div>
                                
                                {/* Dynamic Half-Ring Colored Outline (Top 50% only) */}
                                <div 
                                    className={`absolute -top-20 left-1/2 -translate-x-1/2 w-36 h-36 md:w-44 md:h-44 rounded-full border-[5px] ${card.ringColor} z-20 pointer-events-none`}
                                    style={{ clipPath: 'inset(0 0 50% 0)' }}
                                />

                                <div className="flex-1 flex flex-col items-start text-left">
                                    {/* Number Badge */}
                                    <span className={`inline-flex items-center justify-center px-3 py-1 text-sm md:text-base font-black rounded-md mb-4 ${card.badgeColor}`}>
                                        {card.id}
                                    </span>
                                    {/* Header Title */}
                                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 leading-snug tracking-tight leading-tight">
                                        {card.title}
                                    </h3>
                                    {/* Description */}
                                    <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                                        {card.description}
                                    </p>
                                </div>

                                {/* Dark-to-Light Fading Gradient Indicator Bar */}
                                <div className={`w-20 h-1.5 bg-gradient-to-r ${card.barGradient} rounded-full mt-auto`} />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="flex justify-center mb-4 md:mb-6">
                            <div className="bg-indigo-100 text-indigo-700 text-sm md:text-base font-semibold px-4 py-2 rounded-md">
                                Core Features
                            </div>
                        </div>
                        <h2 className="text-2xl md:text-4xl font-bold text-slate-800 mb-6 tracking-tight leading-tight">
                            Choose Your Perfect Plan
                        </h2>
                    </div>

                    <div className="grid mt-8 md:mt-16 px-4 md:px-10 lg:px-20 gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className={`plan-card-container h-full ${(feature.title === 'Growth' || feature.title === 'Essential') ? 'expanded' : ''
                                    }`}
                            >
                                <div className="plan-card-content">
                                    {feature.title === 'Growth' && (
                                        <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden pointer-events-none z-10">
                                            <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[14px] font-bold py-2 w-[160px] text-center transform translate-x-[25%] translate-y-[70%] rotate-45 shadow-md">
                                                Best Value
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-center h-12 w-12 md:h-16 md:w-16 rounded-xl bg-gray-100 mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3 tracking-tight leading-tight">{feature.title}</h3>
                                    <p className="text-sm font-semibold text-slate-600 leading-relaxed min-h-12">{feature.description}</p>
                                    <div className="h-px bg-gray-200 my-1"></div>
                                    <ul className="mt-4 space-y-3">
                                        {feature.details.map((item, itemIndex) => (
                                            <li key={itemIndex} className="flex items-start gap-3 text-sm md:text-base">
                                                <Check className="h-4 w-4 md:h-5 md:w-5 text-indigo-600 mt-1 flex-shrink-0" />
                                                <span className={item.highlight ? "text-indigo-600 font-bold" : "text-slate-600"}>{item.highlight ? item.text : formatServiceName(item.text)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <Link href="/login" className="inline-block">
                            <button className="mt-10 md:mt-15 bg-indigo-600 text-white py-2.5 md:py-3 px-8 rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors text-sm md:text-base hover:cursor-pointer">
                                Explore More
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </Link>

                        {!showEnterprise && (
                            <div className="mt-10">
                                <motion.button
                                    onClick={handleShowEnterpriseClick}
                                    className="relative z-10 font-semibold text-indigo-700 focus:outline-none bg-transparent border-2 border-indigo-600 rounded-lg py-2 md:py-3 px-2 md:px-10 cursor-pointer transition-all duration-300 ease-in-out hover:bg-indigo-100 text-xs md:text-base"
                                    animate={{
                                        scale: [1, 1.3, 1],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                >
                                    Looking for enterprise-level solutions?
                                    <br className="md:hidden" /> {/* This line break is hidden on medium screens and up */}
                                    Get in touch!
                                </motion.button>
                            </div>
                        )}
                    </div>

                    <AnimatePresence>
                        {showEnterprise && (
                            <section id="enterprise-section" className="scroll-mt-7 bg-slate-50 pt-16 pb-8 md:pt-20 md:pb-4">
                                <motion.div
                                    className="container mx-auto px-4"
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -30 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
                                        <div className="text-left">
                                            <div className="flex items-center gap-4 mb-6 md:mb-8 mt-6">
                                                <Building className="h-6 w-6 md:h-10 md:w-10 text-indigo-600 flex-shrink-0" />
                                                <h2 className="text-2xl md:text-5xl font-bold text-slate-800">
                                                    Enterprise
                                                </h2>
                                            </div>
                                            <p className="text-base md:text-xl font-semibold text-slate-600 mb-6 md:mb-8 pl-1">
                                                Get in touch with us to learn more or to customize a solution that fits your enterprise needs!
                                            </p>
                                            {/* Modified Special Features List */}
                                            <ul className="space-y-2 md:space-y-4 pl-1 md:pl-4">
                                                {enterpriseFeatures.map((feature, index) => {
                                                    if (index === 0) {
                                                        return (
                                                            <li key={index} className="mb-3">
                                                                <span className="text-sm md:text-lg font-bold text-slate-800 underline">
                                                                    {feature.text}
                                                                </span>
                                                            </li>
                                                        );
                                                    }
                                                    return (
                                                        <li key={index} className="flex items-start gap-4">
                                                            <Check className="h-5 w-5 md:h-6 md:w-6 text-indigo-600 mt-1 flex-shrink-0" />
                                                            <span className={`text-sm md:text-lg ${feature.highlight ? "text-indigo-600 font-bold" : "text-slate-700"}`}>
                                                                {feature.text}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>

                                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                                            <form onSubmit={handleSubmit} method="POST" className="space-y-5" noValidate>
                                                <div>
                                                    <label htmlFor="full-name" className="block text-sm md:text-base font-semibold text-slate-700 mb-1">
                                                        Full Name<span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="fullName"
                                                        id="full-name"
                                                        placeholder="Enter your full name"
                                                        className={`w-full px-4 py-2 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.fullName ? 'border-red-500' : 'border-gray-200'}`}
                                                        value={enterpriseForm.fullName}
                                                        onChange={(e) => setEnterpriseForm({ ...enterpriseForm, fullName: e.target.value.toUpperCase() })}
                                                        onBlur={handleBlur}
                                                    />
                                                    {errors.fullName && <p className="text-red-600 text-xs md:text-smmt-1">{errors.fullName}</p>}
                                                </div>

                                                <div>
                                                    <label htmlFor="work-email" className="block text-sm md:text-base font-semibold text-slate-700 mb-1">
                                                        Email<span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        id="work-email"
                                                        placeholder="e.g., user@gmail.com"
                                                        className={`w-full px-4 py-2 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                                                        value={enterpriseForm.email}
                                                        onChange={(e) => setEnterpriseForm({ ...enterpriseForm, email: e.target.value })}
                                                        onBlur={handleBlur}
                                                    />
                                                    {errors.email && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.email}</p>}
                                                </div>

                                                <div>
                                                    <label htmlFor="company-name-enterprise" className="block text-sm md:text-base font-semibold text-slate-700 mb-1">
                                                        Company Name<span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="companyName"
                                                        id="company-name-enterprise"
                                                        placeholder="Enter your company name"
                                                        className={`w-full px-4 py-2 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.companyName ? 'border-red-500' : 'border-gray-200'}`}
                                                        value={enterpriseForm.companyName}
                                                        onChange={(e) => setEnterpriseForm({ ...enterpriseForm, companyName: e.target.value })}
                                                        onBlur={handleBlur}
                                                    />
                                                    {errors.companyName && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.companyName}</p>}
                                                </div>

                                                <div>
                                                    <label htmlFor="phone-number" className="block text-sm md:text-base font-semibold text-slate-700 mb-1">
                                                        Phone Number<span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        name="phoneNumber"
                                                        id="phone-number"
                                                        placeholder="e.g., 60123456789"
                                                        className={`w-full px-4 py-2 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.phoneNumber ? 'border-red-500' : 'border-gray-200'}`}
                                                        value={enterpriseForm.phoneNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            if (value.length <= 12) {
                                                                setEnterpriseForm({ ...enterpriseForm, phoneNumber: value });
                                                            }
                                                        }}
                                                        onBlur={handleBlur}
                                                        maxLength={12}
                                                    />
                                                    {errors.phoneNumber && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.phoneNumber}</p>}
                                                </div>

                                                {/* Business Size */}
                                                <div>
                                                    <CustomSelect
                                                        id="businessSize"
                                                        label={<>Business Size<span className="text-red-500">*</span></>}
                                                        data={businessSizes}
                                                        selected={enterpriseForm.businessSize}
                                                        onChange={(value) => setEnterpriseForm(prev => ({ ...prev, businessSize: value }))}
                                                    />
                                                    {errors.businessSize && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.businessSize}</p>}
                                                </div>

                                                <div>
                                                    <button
                                                        type="submit"
                                                        className="w-full mt-2 bg-indigo-600 text-white font-semibold py-2.5 md:py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors shadow-md disabled:bg-indigo-400 disabled:cursor-not-allowed text-sm md:text-base"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? 'Sending...' : 'Send Email'}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </motion.div>
                            </section>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* --- Stats Section --- */}
            <section ref={statsSectionRef} className="bg-indigo-600 text-white py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-2xl md:text-4xl font-bold text-center mb-12 tracking-tight leading-tight">Fueling the Future of Sarawak’s SMEs</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-xs md:max-w-4xl mx-auto mb-12">
                        {[
                            { label: 'Save Cost', val: 20, suffix: '%' },
                            { label: 'Services', val: 30, suffix: '+' },
                            { label: 'Training', val: 20, suffix: '+' },
                        ].map((stat, i) => (
                            <div key={i} className="bg-white text-indigo-700 p-8 rounded-2xl text-center shadow-xl">
                                <p className="text-4xl md:text-5xl font-extrabold mb-2">
                                    <Counter end={stat.val} duration={2000} start={startAnimation} suffix={stat.suffix} />
                                </p>
                                <p className="text-lg md:text-xl text-black font-medium">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white text-slate-800 p-6 md:p-8 rounded-2xl max-w-xs md:max-w-4xl mx-auto text-center shadow-xl">
                        <h3 className="text-xl md:text-2xl font-bold mb-4 tracking-tight leading-tight">Want to know more about our product?</h3>
                        <p className="text-sm md:text-base text-slate-600 mb-8 max-w-md mx-auto">
                            Create your free account to unlock comprehensive details about our services and see how we can support your goals.
                        </p>
                        <Link href="/login">
                            <button className="bg-indigo-600 text-white font-semibold py-2.5 md:py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base hover:cursor-pointer">
                                Get Free Access Now
                            </button>
                        </Link>
                    </div>
                </div>
            </section>            

            {/* --- Services & Training Program Section --- */}
            <section className="bg-white py-16 md:py-20">
                {/* --- Our Services Section --- */}
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-2xl md:text-4xl font-bold text-slate-800 tracking-tight leading-tight">
                            Our Services
                        </h2>
                    </div>

                    <div className="scroller-container mt-2 md:mt-4 space-y-8 py-6 md:py-8">
                        {/* Row 1: Scrolls Left */}
                        <div className="scroller-row scroll-left">
                            {[...servicesRow1, ...servicesRow1].map((service, index) => (
                                <div key={`row1-${index}`} className="our-service-card-container flex-shrink-0">
                                    <div className="our-service-card">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="flex items-center justify-center h-12 w-12 md:h-16 md:w-16 rounded-xl bg-indigo-50 flex-shrink-0">
                                                <DynamicIcon name={service.icon} className="h-6 w-6 md:h-8 md:w-8 text-indigo-600" />
                                            </div>
                                            <h3 className="text-base md:text-xl font-bold text-slate-800 tracking-tight leading-tight">
                                                {formatServiceName(service.serviceName)}
                                            </h3>
                                        </div>
                                        <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                                            {service.summary || 'No summary available'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Row 2: Scrolls Right */}
                        <div className="scroller-row scroll-right">
                            {[...servicesRow2, ...servicesRow2].map((service, index) => (
                                <div key={`row2-${index}`} className="our-service-card-container flex-shrink-0">
                                    <div className="our-service-card">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="flex items-center justify-center h-12 w-12 md:h-16 md:w-16 rounded-xl bg-indigo-50 flex-shrink-0">
                                                <DynamicIcon name={service.icon} className="h-6 w-6 md:h-8 md:w-8 text-indigo-600" />
                                            </div>
                                            <h3 className="text-base md:text-xl font-bold text-slate-800 tracking-tight leading-tight">
                                                {formatServiceName(service.serviceName)}
                                            </h3>
                                        </div>
                                        <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                                            {service.summary || 'No summary available'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-center">
                        <Link href="/login" className="inline-block">
                            <button className="mt-4 bg-indigo-600 text-white py-2.5 md:py-3 px-8 rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors text-sm md:text-base hover:cursor-pointer">
                                Explore More
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </Link>
                    </div>
                </div>

                {/* --- Training Program Section --- */}
                <div className="container mx-auto px-4 pt-20">
                    <div className="max-w-4xl mx-auto mb-10 text-center">
                        <h2 className="text-2xl md:text-4xl font-bold text-slate-800 tracking-tight leading-tight">
                            Training Program
                        </h2>
                    </div>

                    {/* Unified 2-Column Responsive Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 md:px-12 max-w-7xl mx-auto">
                        {trainingPrograms.map((program, index) => (
                            <div key={index} className="training-card-container">
                                {/* Snapped row container keeping your custom CSS glow style */}
                                <div className="training-card-content flex flex-row items-stretch h-56 md:h-60 overflow-hidden rounded-2xl">
                                    
                                    {/* Left Image: Securely stuck with no white gap */}
                                    <div className="relative w-2/5 flex-shrink-0 h-full overflow-hidden">
                                        <Image
                                            src={program.image}
                                            alt={program.title}
                                            fill
                                            className="object-cover"
                                        />
                                    </div>

                                    {/* Right Content */}
                                    <div className="flex-1 p-5 md:p-6 flex flex-col justify-between bg-white text-left">
                                        <div>
                                            <h3 className="text-base md:text-xl font-bold text-slate-800 mb-2 leading-snug tracking-tight leading-tight">
                                                {program.title}
                                            </h3>
                                            <p className="text-xs md:text-base text-slate-600 leading-relaxed">
                                                {program.description}
                                            </p>
                                        </div>
                                        {/* Certifications Label aligned bottom right in indigo-700 */}
                                        <p className="text-xs md:text-sm text-indigo-700 font-medium text-right mt-2 self-end">
                                            (Certifications - {program.certifications})
                                        </p>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link href="/login" className="inline-block">
                            <button className="bg-indigo-600 text-white py-2.5 md:py-3 px-8 rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors text-sm md:text-base hover:cursor-pointer">
                                Explore More
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </Link>
                    </div>
                </div>

                {/* --- Not Sure Where to Start Section --- */}
                <div className="container mx-auto px-4 mt-20 max-w-5xl">
                    <div className="bg-indigo-600 text-white rounded-3xl py-12 px-6 md:py-16 md:px-12 text-center shadow-md">
                        <h3 className="text-2xl md:text-4xl font-bold mb-3 tracking-tight leading-tight">
                            Not Sure Where to Start? No Worries!
                        </h3>
                        <p className="text-sm md:text-base text-indigo-100 mb-8">
                            Let us guide your digital journey with expert support and simple steps
                        </p>
                        <Link href="/support" className="inline-block">
                            <button className="border border-white text-white font-semibold py-2.5 md:py-3 px-8 rounded-lg hover:bg-white/10 text-sm md:text-base cursor-pointer transition-colors">
                                Get Support
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* --- CSS styles  --- */}
            <style jsx>{`
                .plan-card-container {
                    position: relative;
                    z-index: 1;
                }
                .plan-card-container.expanded {
                    z-index: 10;
                }
                .plan-card-content {
                    position: relative;
                    overflow: hidden;
                    background-color: white;
                    border-radius: 1rem;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                    text-align: left;
                    display: flex;
                    flex-direction: column;
                    border: 1px solid #e0e7ff;
                    padding: 2rem;
                    transition: all 0.3s ease-in-out;
                    height: 100%;
                }
                .plan-card-container::before {
                    content: '';
                    position: absolute;
                    inset: -1px;
                    background-image: linear-gradient(to right, #d946ef, #3b82f6);
                    border-radius: 1rem;
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    filter: blur(16px);
                }
                .plan-card-container:hover .plan-card-content {
                    transform: translateY(-0.5rem);
                    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
                }
                .plan-card-container:hover::before {
                    opacity: 0.75;
                }

                .training-card-container {
                    position: relative;
                    z-index: 1;
                }
                .training-card-content {
                    background-color: white;
                    border-radius: 1rem;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                    transition: all 0.3s ease-in-out;
                    height: 100%;
                }
                .training-card-container::before {
                    content: '';
                    position: absolute;
                    inset: -1px;
                    background-image: linear-gradient(to right, #00c6ff, #0072ff);
                    border-radius: 1rem;
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    filter: blur(16px);
                }
                .training-card-container:hover .training-card-content {
                    transform: translateY(-0.5rem);
                    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
                }
                .training-card-container:hover::before {
                    opacity: 0.75;
                }

                .our-service-card-container {
                    position: relative;
                    z-index: 0;
                    width: 320px; 
                    margin: 0 0.75rem;
                }
                .our-service-card {
                    background-color: white;
                    border-radius: 1rem;
                    padding: 1.5rem; 
                    text-align: left;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    min-height: 180px; 
                    border: 1px solid #f1f5f9;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                    transition: all 0.3s ease-in-out;
                }
                .our-service-card-container::before {
                    content: '';
                    position: absolute;
                    inset: -1px;
                    background-image: linear-gradient(to right, #8b5cf6, #22d3ee);
                    border-radius: 1rem;
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    filter: blur(16px);
                }
                .our-service-card-container:hover .our-service-card {
                    transform: translateY(-0.5rem);
                    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
                }
                .our-service-card-container:hover::before {
                    opacity: 0.75;
                }

                @keyframes scroll-left {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }

                @keyframes scroll-right {
                    from { transform: translateX(-50%); }
                    to { transform: translateX(0); }
                }

                .scroller-container {
                    overflow: hidden;
                    -webkit-mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent);
                    mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent);
                }
                .scroller-row {
                    display: flex;
                    width: max-content;
                    animation-timing-function: linear;
                    animation-iteration-count: infinite;
                }
                .scroll-left {
                    animation-name: scroll-left;
                    animation-duration: 60s;
                }
                .scroll-right {
                    animation-name: scroll-right;
                    animation-duration: 60s;
                }

                @media (min-width: 768px) {
                    .our-service-card {
                        padding: 2rem;
                        min-height: 220px;
                    }
                    .scroll-left,
                    .scroll-right {
                        animation-duration: 40s;
                    }
                    .our-service-card-container {
                        width: 400px;
                        margin: 0 1rem;
                    }
                } 

                @media (min-width: 1280px) {
                    .plan-card-container.expanded {
                        margin-top: -1.5rem;
                        margin-bottom: -1.5rem;
                        height: calc(100% + 3rem);
                    }
                }
            `}</style>
        </>
    );
};

export default HeroBefore;